import {
  firstValueFrom, Observable, MonoTypeOperatorFunction, BehaviorSubject
} from 'rxjs';
import { filter, take, switchMap } from 'rxjs/operators';

import { FlagObservable } from './flag-observable';

/**
 * a FlagSubject acts like a behavior subject, except it provides a
 * set/reset mechanic, and 'await' methods, which wait for the flag to
 * be pass a predicate (truthy by default).
 */
export class FlagSubject<T> implements FlagObservable<T> {
  private subject: BehaviorSubject<T>;
  private predicate: (T) => boolean;

  /**
     * @param initial - the initial state of the flag
     * @param predicate - an optional predicate function for determining if the flag is set.
     */
  constructor(
    initial: T,
    predicate?: (T) => boolean
  ) {
    this.subject = new BehaviorSubject<T>(initial);
    this.predicate = predicate;
  }

  get value() {
    return this.subject.getValue();
  }

  next(value: T) {
    this.subject.next(value);
  }

  asObservable() {
    return this.subject.asObservable();
  }

  awaiter(): Observable<T> {
    return this.subject.pipe(
      filter<T>(this.predicate || Boolean),
      take(1)
    );
  }

  awaitNegative(): Observable<T> {
    return this.subject.pipe(
      filter((x) => {
        const c = (this.predicate || Boolean)(x);
        return !c;
      }),
      take(1)
    );
  }

  waitP(): Promise<T | undefined> {
    return firstValueFrom(this.awaiter());
  }

  waitO<U>(): MonoTypeOperatorFunction<U> {
    return (src: Observable<U>) => this.awaiter().pipe(switchMap(() => src));
  }
}
